import {
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

import { ICountry } from '@ib-shared/interfaces/country.intf';
import { CountriesHttpService } from '@ib-shared/services/countries-data.service';
import {
  combineLatest,
  debounceTime,
  filter,
  map,
  Observable,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

@Component({
  selector: 'ib-country',
  templateUrl: './country.component.html',
})
export class CountryComponent implements OnInit, OnDestroy {
  @Input() formCtrl!: AbstractControl;
  @Input() isRequired: boolean;

  @Input() initCountryByIp = true;

  private destroy$ = new Subject<void>();
  private allCountries: Array<ICountry>;
  private allCountries$: Observable<Array<ICountry>>;
  public searchCtrl = new FormControl('');
  public filteredCountries$: Observable<Array<ICountry>>;

  @HostBinding('class') get classes(): string {
    return [
      this.formCtrl?.touched ? 'ib-country--touched' : null,
      this.formCtrl?.invalid ? 'ib-country--invalid' : null,
    ]
      .filter(i => i)
      .join(' ');
  }

  constructor(private countriesHttpS: CountriesHttpService) {}

  ngOnInit(): void {
    this.initCountryOptions();
    if (!this.isCountryDefined() && this.initCountryByIp) {
      this.initByIp();
    }
    this.initClearValueAfterResetParentForm();
    this.initDefaultSearchName();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initClearValueAfterResetParentForm(): void {
    this.formCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(v => {
      if (v === '' || v === null || typeof v === 'undefined') {
        this.searchCtrl.setValue(undefined);
      }
    });
  }

  private initDefaultSearchName(): void {
    combineLatest([
      this.formCtrl.valueChanges.pipe(filter(r => Boolean(r))),
      this.countriesHttpS.selectAllCountries().pipe(
        filter(r => Boolean(r.length)),
        tap(r => {
          this.allCountries = r;
        }),
      ),
    ])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(([countryCode, countryList]) => {
        const defSearchName = countryList?.find(
          c => c.code.toLowerCase() === countryCode.toLowerCase(),
        ).name;
        this.searchCtrl.setValue(defSearchName);
      });
  }

  private isCountryDefined(): boolean {
    return Boolean(this.formCtrl.value);
  }

  private initByIp(): void {
    this.countriesHttpS
      .selectUserCountryByIp()
      .pipe(
        switchMap(dataByIp => {
          return this.countriesHttpS.selectAllCountries().pipe(
            filter(countryList => Boolean(countryList.length)),
            map(countryList => {
              return countryList.find(
                i =>
                  i.code.toLowerCase() === dataByIp.country_code.toLowerCase(),
              );
            }),
          );
        }),
      )
      .subscribe(r => {
        this.searchCtrl.setValue(r?.name);
        this.formCtrl.setValue(r?.code?.toLowerCase());
      });
  }

  private initCountryOptions(): void {
    this.allCountries$ = this.countriesHttpS.selectAllCountries();
    this.filteredCountries$ = combineLatest([
      this.allCountries$,
      this.searchCtrl.valueChanges.pipe(startWith('')),
    ]).pipe(
      debounceTime(50),
      map(([countries, searchVal]) => {
        const list = countries?.length
          ? countries.filter(i => {
              return searchVal
                ? i.name
                    ?.toLowerCase()
                    .startsWith(searchVal.toLowerCase() || '')
                : i;
            })
          : [];
        return list;
      }),
    );
  }

  selected(v: MatAutocompleteSelectedEvent): void {
    const code = this.allCountries
      .find(i => i.name === v.option.value)
      .code.toLowerCase();
    this.formCtrl.setValue(code);
  }
}
